import { ASSET_TYPE, PRINT_TYPE } from "./rrdConstants";

export const RRD_LAYOUT_KEY = {
  BASIC_POSTCARD_1H_6C: "BASIC_POSTCARD_1H_6C",
  BASIC_POSTCARD_1H_4C_FIXED: "BASIC_POSTCARD_1H_4C_FIXED",
  BASIC_POSTCARD_1H_3C_FIXED: "BASIC_POSTCARD_1H_3C_FIXED",
  MEGA_SHEET_1L_1M_8C: "MEGA_SHEET_1L_1M_8C",
  DYNAMIC_POSTCARD_1H_6C_FIXED: "DYNAMIC_POSTCARD_1H_6C_FIXED",
  VALUE_SHEET_INSERT_2H_12C: "VALUE_SHEET_INSERT_2H_12C",
};

export const RRD_EXPIRY_MODE = {
  SINGLE: "single",
  FRONT_BACK: "front_back",
};

const squareCoupon = { aspectRatio: "1 / 1" };

const createCouponSlots = ({
  count,
  side = "front",
  editable = true,
  slotPrefix = "coupon",
  aspectRatio = squareCoupon.aspectRatio,
}) =>
  Array.from({ length: count }, (_, order) => ({
    id: `${slotPrefix}_${order}`,
    side,
    assetType: ASSET_TYPE.COUPON,
    order,
    editable,
    aspectRatio,
  }));

const heroSlot = ({
  id,
  side,
  assetType = ASSET_TYPE.HERO,
  aspectRatio = "14 / 9",
}) => ({
  id,
  side,
  assetType,
  aspectRatio,
});

const fixedSlot = ({ id = "fixed_image_back", side = "back" } = {}) => ({
  id,
  side,
  assetType: ASSET_TYPE.FIXED_IMAGE,
  locked: true,
  aspectRatio: "1 / 1",
});

export const RRD_LEGACY_PRINT_TYPE_LAYOUT = {
  [PRINT_TYPE.POSTCARD.value]: RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_6C,
  [PRINT_TYPE.POSTCARD.label]: RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_6C,
  [PRINT_TYPE.BASIC_POSTCARD.value]: RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_6C,
  [PRINT_TYPE.MEGA_SHEET.value]: RRD_LAYOUT_KEY.MEGA_SHEET_1L_1M_8C,
  [PRINT_TYPE.DYNAMIC_POSTCARD.value]:
    RRD_LAYOUT_KEY.DYNAMIC_POSTCARD_1H_6C_FIXED,
  [PRINT_TYPE.VALUE.value]: RRD_LAYOUT_KEY.VALUE_SHEET_INSERT_2H_12C,
  [PRINT_TYPE.VALUE.label]: RRD_LAYOUT_KEY.VALUE_SHEET_INSERT_2H_12C,
};

export const RRD_LAYOUTS = {
  [RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_6C]: {
    layoutKey: RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_6C,
    printTypeLabel: PRINT_TYPE.POSTCARD.label,
    legacyPrintType: PRINT_TYPE.POSTCARD.value,
    fixedImageRequired: false,
    expiryMode: RRD_EXPIRY_MODE.SINGLE,
    requiredAssets: { heroImages: 1, coupons: 6 },
    completionAssets: { heroImages: 1, coupons: 6 },
    selectableAssetTypes: [ASSET_TYPE.HERO, ASSET_TYPE.COUPON],
    couponGrid: { columns: 3, rows: 2 },
    slots: [
      heroSlot({
        id: "hero_front",
        side: "front",
        aspectRatio: "9 / 10.22",
      }),
      ...createCouponSlots({ count: 6, side: "front" }),
    ],
  },
  [RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_4C_FIXED]: {
    layoutKey: RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_4C_FIXED,
    printTypeLabel: PRINT_TYPE.POSTCARD.label,
    fixedImageRequired: true,
    expiryMode: RRD_EXPIRY_MODE.SINGLE,
    requiredAssets: { heroImages: 1, coupons: 4, fixedImages: 1 },
    completionAssets: { heroImages: 1, coupons: 4 },
    selectableAssetTypes: [ASSET_TYPE.HERO, ASSET_TYPE.COUPON],
    couponGrid: { columns: 2, rows: 2 },
    slots: [
      heroSlot({ id: "hero_front", side: "front" }),
      ...createCouponSlots({ count: 4, side: "back" }),
      fixedSlot(),
    ],
  },
  [RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_3C_FIXED]: {
    layoutKey: RRD_LAYOUT_KEY.BASIC_POSTCARD_1H_3C_FIXED,
    printTypeLabel: PRINT_TYPE.POSTCARD.label,
    faceAspectRatio: "7.55 / 4.5",
    backPanelColumns: "3.6fr 3.95fr",
    fixedImageAspectRatio: "3.95 / 2.5",
    fixedImageRequired: true,
    expiryMode: RRD_EXPIRY_MODE.SINGLE,
    requiredAssets: { heroImages: 1, coupons: 3, fixedImages: 1 },
    completionAssets: { heroImages: 1, coupons: 3 },
    selectableAssetTypes: [ASSET_TYPE.HERO, ASSET_TYPE.COUPON],
    couponGrid: { columns: 1, rows: 3 },
    slots: [
      heroSlot({ id: "hero_front", side: "front" }),
      ...createCouponSlots({
        count: 3,
        side: "back",
        aspectRatio: "2.4 / 1",
      }),
      fixedSlot(),
    ],
  },
  [RRD_LAYOUT_KEY.MEGA_SHEET_1L_1M_8C]: {
    layoutKey: RRD_LAYOUT_KEY.MEGA_SHEET_1L_1M_8C,
    printTypeLabel: "Mega Sheet",
    fixedImageRequired: false,
    expiryMode: RRD_EXPIRY_MODE.SINGLE,
    requiredAssets: { largeHeroImages: 1, mediumHeroImages: 1, coupons: 8 },
    completionAssets: { largeHeroImages: 1, mediumHeroImages: 1, coupons: 8 },
    selectableAssetTypes: [
      ASSET_TYPE.LARGE_HERO,
      ASSET_TYPE.MEDIUM_HERO,
      ASSET_TYPE.COUPON,
    ],
    couponGrid: { columns: 4, rows: 2 },
    slots: [
      heroSlot({
        id: "large_hero_front",
        side: "front",
        assetType: ASSET_TYPE.LARGE_HERO,
        aspectRatio: "10 / 4.5",
      }),
      heroSlot({
        id: "medium_hero_back",
        side: "back",
        assetType: ASSET_TYPE.MEDIUM_HERO,
        aspectRatio: "10 / 3.25",
      }),
      ...createCouponSlots({ count: 8, side: "back" }),
    ],
  },
  [RRD_LAYOUT_KEY.DYNAMIC_POSTCARD_1H_6C_FIXED]: {
    layoutKey: RRD_LAYOUT_KEY.DYNAMIC_POSTCARD_1H_6C_FIXED,
    printTypeLabel: "Dynamic Postcard",
    fixedImageRequired: true,
    expiryMode: RRD_EXPIRY_MODE.SINGLE,
    requiredAssets: { heroImages: 1, coupons: 6, fixedImages: 1 },
    completionAssets: { heroImages: 1, coupons: 6 },
    selectableAssetTypes: [ASSET_TYPE.HERO, ASSET_TYPE.COUPON],
    couponGrid: { columns: 3, rows: 2 },
    slots: [
      heroSlot({
        id: "hero_front",
        side: "front",
        aspectRatio: "11 / 6",
      }),
      ...createCouponSlots({ count: 6, side: "back" }),
      fixedSlot(),
    ],
  },
  [RRD_LAYOUT_KEY.VALUE_SHEET_INSERT_2H_12C]: {
    layoutKey: RRD_LAYOUT_KEY.VALUE_SHEET_INSERT_2H_12C,
    printTypeLabel: PRINT_TYPE.VALUE.label,
    legacyPrintType: PRINT_TYPE.VALUE.value,
    fixedImageRequired: false,
    expiryMode: RRD_EXPIRY_MODE.FRONT_BACK,
    requiredAssets: { heroImages: 2, coupons: 12 },
    completionAssets: { heroImages: 2, coupons: 6 },
    selectableAssetTypes: [ASSET_TYPE.HERO, ASSET_TYPE.COUPON],
    couponGrid: { columns: 3, rows: 2 },
    slots: [
      heroSlot({ id: "hero_front", side: "front" }),
      ...createCouponSlots({ count: 6, side: "front" }),
      heroSlot({ id: "hero_back", side: "back" }),
      ...createCouponSlots({
        count: 6,
        side: "back",
        editable: false,
        slotPrefix: "coupon_back_preview",
      }),
    ],
  },
};
